/* R46: visible defaults, native navigation, one owner for decorative motion. */
(function () {
  'use strict';
  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var active = new Set();
  var seen = new WeakSet();
  var inView = new WeakSet();
  var byElement = new WeakMap();
  var observer;
  var lenis;
  var frame;
  var wakeScroll = function () {};
  var editorialAnchors = '#MainContent, #mgo-stufen, #laborberichte, #mgo-umf, #sortiment, #auswahlhilfe, #fragen';
  var targets = [
    '.section-header__title', '.stufen-finder__intro', '.ms-aktuell__card',
    '.text-with-icons__block', '.manuka-mgo__section-head',
    '.manuka-mgo__compare-intro', '.manuka-mgo__compare-text',
    '.manuka-mgo__proof-card', '.manuka-about__section-head',
    '.ms-laborberichte', '.grid-product', '.promo-grid__container',
    '.ms-aktuell__header', '.manuka-about__text-block', '.manuka-about__media',
    '.manuka-about__value-card', '.manuka-about__market-card',
    '.manuka-markets__event', '.manuka-markets__section-head',
    '.manuka-markets__quick-card', '.manuka-markets__hero-content',
    '.manuka-about__hero-content', '.ms-guide__header', '.ms-guide-card', '.ms-guide__note'
  ].join(', ');

  function animate(element, keyframes, options) {
    if (reduced.matches || !element.animate || document.hidden) return;
    var previous = byElement.get(element);
    if (previous) previous.cancel();
    var animation = element.animate(keyframes, options);
    byElement.set(element, animation);
    active.add(animation);
    animation.finished.then(function () {
      active.delete(animation);
      if (byElement.get(element) === animation && outsideAtRest(element)) inView.delete(element);
    }, function () { active.delete(animation); });
  }

  function outsideAtRest(element) {
    var box = element.getBoundingClientRect();
    var offset = 0;
    var animation = byElement.get(element);
    // The fly-in itself must not turn a short title into a new observer entry.
    if (animation && animation.playState === 'running' && window.DOMMatrixReadOnly) {
      var transform = getComputedStyle(element).transform;
      if (transform !== 'none') offset = new DOMMatrixReadOnly(transform).m42;
    }
    return box.bottom - offset <= 0 || box.top - offset >= window.innerHeight - 24;
  }

  function cancelMotion() {
    if (observer) observer.disconnect();
    seen = new WeakSet();
    inView = new WeakSet();
    active.forEach(function (animation) { animation.cancel(); });
    active.clear();
  }

  function reveal(element, delay) {
    if (element.contains(document.activeElement) || element.matches(':target')) return;
    animate(element, [
      { opacity: 0, transform: 'translateY(40px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 1100, delay: delay || 0, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' });
  }

  function initReveals() {
    if (reduced.matches || !('IntersectionObserver' in window)) return;
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        var sequence = 0;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            if (outsideAtRest(entry.target)) inView.delete(entry.target);
            return;
          }
          if (entry.intersectionRatio < .12 || inView.has(entry.target)) return;
          inView.add(entry.target);
          reveal(entry.target, Math.min(sequence++ * 90, 270));
        });
      }, { threshold: [0, .12], rootMargin: '0px 0px -24px 0px' });
    }
    document.querySelectorAll('#MainContent ' + targets.split(', ').join(', #MainContent ')).forEach(function (element) {
      if (seen.has(element) || element.closest('[data-ms-motion-hero], .ms-wissen, .flickity-slider, .drawer, .modal, [data-ms-enter], [data-ms-enter-image]') || element.parentElement.closest('[data-ms-fly]')) return;
      seen.add(element);
      var box = element.getBoundingClientRect();
      if (!box.height) return;
      element.setAttribute('data-ms-fly', '');
      observer.observe(element);
    });
  }

  function heroEntrance() {
    var navigation = performance.getEntriesByType('navigation')[0];
    if (location.hash || window.scrollY > 60 || (navigation && navigation.type === 'back_forward')) return;
    document.querySelectorAll('[data-ms-enter]').forEach(function (element, index) { reveal(element, Math.min(index * 110, 330)); });
    document.querySelectorAll('[data-ms-enter-image]').forEach(function (element) {
      animate(element, [{ opacity: .45, transform: 'translateY(32px) scale(1.04)' }, { opacity: 1, transform: 'translateY(0)' }], {
        duration: 1300, easing: 'cubic-bezier(.22,1,.36,1)'
      });
    });
  }

  function updateOffset() {
    var header = document.getElementById('SiteHeader');
    if (header) root.style.setProperty('--ms-anchor-offset', Math.ceil(header.getBoundingClientRect().height + 16) + 'px');
  }

  function destroyScroll() {
    cancelAnimationFrame(frame);
    frame = null;
    if (lenis) lenis.destroy();
    lenis = null;
    wakeScroll = function () {};
  }

  function initScroll() {
    if (reduced.matches || !finePointer.matches || !window.Lenis || (window.Shopify && Shopify.designMode)) return;
    lenis = new window.Lenis({
      autoRaf: false,
      lerp: .13,
      smoothWheel: true,
      syncTouch: false,
      anchors: false,
      stopInertiaOnNavigate: true,
      prevent: function (node) {
        return node.matches && node.matches('.drawer, .modal, [role="dialog"], [data-lenis-prevent], textarea, select, .flickity-viewport');
      },
      virtualScroll: function (event) {
        return !event.event.ctrlKey && !event.event.shiftKey && !root.matches('.js-drawer-open, .js-modal-open') && !document.body.matches('.modal-open') && !document.querySelector('dialog[open], .modal--is-active');
      }
    });
    // Run frames only while wheel/programmatic inertia is moving, not while idle.
    var clock = 0;
    var previousTime = 0;
    function tick(time) {
      frame = null;
      if (!lenis) return;
      clock += previousTime ? Math.min(time - previousTime, 32) : 16;
      previousTime = time;
      lenis.raf(clock);
      if (lenis.isScrolling === 'smooth') frame = requestAnimationFrame(tick);
    }
    wakeScroll = function () {
      if (!frame) { previousTime = 0; frame = requestAnimationFrame(tick); }
    };
    lenis.on('virtual-scroll', wakeScroll);
  }

  function focusAnchor(target) {
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
      target.addEventListener('blur', function () { target.removeAttribute('tabindex'); }, { once: true });
    }
    target.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    var url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash) return;
    var id;
    try { id = decodeURIComponent(url.hash.slice(1)); } catch (_) { return; }
    var target = document.getElementById(id);
    if (!target || !target.matches(editorialAnchors)) return;
    cancelMotion();
    updateOffset();
    if (lenis) {
      event.preventDefault();
      if (location.hash !== url.hash) history.pushState(null, '', url.hash);
      focusAnchor(target);
      lenis.scrollTo(target, {
        onStart: wakeScroll
      });
    } else {
      // Keep native hash navigation and its scroll; only move keyboard focus.
      focusAnchor(target);
    }
    initReveals();
  });

  document.addEventListener('focusin', function (event) {
    active.forEach(function (animation) {
      if (animation.effect && animation.effect.target.contains(event.target)) animation.cancel();
    });
  });

  function stopInertia() {
    if (lenis) lenis.scrollTo(window.scrollY, { immediate: true });
    cancelAnimationFrame(frame);
    frame = null;
  }
  document.addEventListener('drawerOpen', stopInertia);
  document.addEventListener('modalOpen', stopInertia);

  function preferenceChanged() {
    cancelMotion();
    destroyScroll();
    initScroll();
    initReveals();
  }
  reduced.addEventListener('change', preferenceChanged);
  finePointer.addEventListener('change', function () { destroyScroll(); initScroll(); });
  document.addEventListener('visibilitychange', function () { if (document.hidden) cancelMotion(); else initReveals(); });
  window.addEventListener('pagehide', function () { cancelMotion(); destroyScroll(); });
  window.addEventListener('pageshow', function (event) { if (event.persisted) { initScroll(); initReveals(); } });
  window.addEventListener('hashchange', function () { if (lenis) lenis.scrollTo(window.scrollY, { immediate: true }); });
  document.addEventListener('shopify:section:load', initReveals);

  function init() {
    updateOffset();
    var header = document.getElementById('SiteHeader');
    if (header && window.ResizeObserver) new ResizeObserver(updateOffset).observe(header);
    initScroll();
    initReveals();
    heroEntrance();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
